import { MISSING_PRIOR_STAGE_LABEL } from '../../utils/formatters';

/**
 * GP-1430 — what belongs in the Order Timeline's "Source → Destination" column.
 *
 * The column is headed with an arrow, but most rows used to render a single chip:
 * a `Troubleshooting Entered` row showed only `Packaging`, so a reader could follow
 * an order OUT of a stage and never see where it went — and NKS has several
 * troubleshooting queues (billing / scheduling / clinical / packaging), so "sent to
 * troubleshooting" alone does not identify the destination. Non-transition rows (SMS,
 * Descartes, system notes) rendered a lone category chip in that same column, which
 * read as a half-filled transition rather than "this event moved nothing".
 *
 * This module answers one question per event — what are its two endpoints? — so the
 * renderer stays a dumb two-slot layout and the rules are unit-testable.
 *
 * A missing endpoint is NEVER invented here: an event with no recorded destination
 * renders an em dash, not a guess. Historical troubleshooting rows written before the
 * destination was persisted legitimately have none.
 */

/** Stage key → display label. Single source for every stage chip on the timeline. */
const STAGE_LABELS: Record<string, string> = {
  billing: 'Billing',
  pending: 'Pending',
  clinic_scheduling: 'Clinic Scheduling',
  clinical_verification: 'Clinical Verification',
  packaging: 'Packaging',
  product_verification: 'Product Verification',
  ready_to_ship: 'Ready to Ship',
  staging: 'Staging',
  out_for_delivery: 'Out for Delivery',
  delivered: 'Delivered',
  delivery_missed: 'Delivery Missed',
  ready_for_pickup: 'Ready for Pickup',
  picked_up: 'Picked Up',
  cancelled: 'Cancelled',
  troubleshooting: 'Troubleshooting',
};

/**
 * Format a raw stage key as its display label — `clinic_scheduling` must never reach
 * the screen. Unknown keys fall back to Title Case rather than being hidden, so a new
 * backend stage is readable the day it ships instead of silently blank.
 */
export const formatStageName = (stage: string): string => {
  if (!stage) return 'Unknown';
  return (
    STAGE_LABELS[stage] ||
    stage
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  );
};

export type TimelineEndpointKind =
  /** A real workflow stage. */
  | 'stage'
  /** A troubleshooting QUEUE (stage + in-troubleshooting), e.g. "Billing Troubleshooting". */
  | 'troubleshooting'
  /** The stage is known to be lost — GP-1334's named data gap, not a legitimate state. */
  | 'missing'
  /** The order did not come from anywhere: this event is its creation. */
  | 'origin'
  /** Nothing recorded for this side. Renders as an em dash. */
  | 'none';

export interface TimelineEndpoint {
  kind: TimelineEndpointKind;
  label: string;
}

export interface TimelineEndpoints {
  /** True when this event moved the order between stages/queues. */
  isTransition: boolean;
  source: TimelineEndpoint;
  destination: TimelineEndpoint;
}

/** Shape of the fields this module reads off a timeline event. */
export interface TimelineEndpointEvent {
  eventType?: string;
  fromStage?: string | null;
  toStage?: string | null;
  metadata?: { drugStageChange?: boolean; [key: string]: any } | null;
}

const NONE: TimelineEndpoint = { kind: 'none', label: '—' };

/** Events whose SOURCE is a troubleshooting queue rather than a plain stage. */
const TROUBLESHOOTING_SOURCE_EVENTS = new Set([
  'troubleshooting_resolved',
  'troubleshooting_stage_moved',
]);

/** Events whose DESTINATION is a troubleshooting queue rather than a plain stage. */
const TROUBLESHOOTING_DESTINATION_EVENTS = new Set([
  'troubleshooting_entered',
  'troubleshooting_stage_moved',
]);

const troubleshootingLabel = (stage: string): string =>
  `${formatStageName(stage)} Troubleshooting`;

export const getTimelineEndpoints = (
  event: TimelineEndpointEvent,
): TimelineEndpoints => {
  const eventType = event?.eventType ?? '';
  const fromStage = event?.fromStage || '';
  const toStage = event?.toStage || '';
  // GP-1334: a per-drug move always describes a transition, even when its source
  // stage was lost — that gap is the thing worth showing, not a reason to hide it.
  const isDrugStageChange = event?.metadata?.drugStageChange === true;

  if (!fromStage && !toStage && !isDrugStageChange) {
    return { isTransition: false, source: NONE, destination: NONE };
  }

  let source: TimelineEndpoint = NONE;
  if (fromStage) {
    source = TROUBLESHOOTING_SOURCE_EVENTS.has(eventType)
      ? { kind: 'troubleshooting', label: troubleshootingLabel(fromStage) }
      : { kind: 'stage', label: formatStageName(fromStage) };
  } else if (isDrugStageChange) {
    source = { kind: 'missing', label: MISSING_PRIOR_STAGE_LABEL };
  } else if (eventType === 'order_created' && toStage) {
    // The order didn't come from a stage — it started here. Say that rather than
    // leaving the reader to wonder what the blank means.
    source = { kind: 'origin', label: 'Order created' };
  }

  let destination: TimelineEndpoint = NONE;
  if (toStage) {
    destination = TROUBLESHOOTING_DESTINATION_EVENTS.has(eventType)
      ? { kind: 'troubleshooting', label: troubleshootingLabel(toStage) }
      : { kind: 'stage', label: formatStageName(toStage) };
  }

  return { isTransition: true, source, destination };
};
